import { useCallback, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import {
  timetableDataQualityApi,
  type CodeConcept,
  type CodeConceptKind,
  type ConceptPiece,
} from '@/services/api/timetableDataQuality'
import { extractErrorMessage } from '@/utils/errorHandler'

const CLASS_LABELS: { value: string; label: string }[] = [
  { value: 'faculty', label: 'Fakultät' },
  { value: 'year', label: 'Jahrgang' },
  { value: 'track', label: 'Vertiefung' },
  { value: 'letter', label: 'Klassenbuchstabe' },
  { value: 'ignore', label: 'ignorieren' },
]

const ROOM_LABELS: { value: string; label: string }[] = [
  { value: 'prefix', label: 'Prefix' },
  { value: 'number', label: 'Nummer' },
  { value: 'ignore', label: 'ignorieren' },
]

const LABEL_TEXT: Record<string, string> = Object.fromEntries(
  [...CLASS_LABELS, ...ROOM_LABELS].map((row) => [row.value, row.label])
)

const SEP_CHARS = new Set([' ', '\t', '.', '-', '_', '/'])

function isSeparatorText(text: string): boolean {
  return text.length > 0 && Array.from(text).every((ch) => SEP_CHARS.has(ch))
}

function applySeparatorIgnore(rows: ConceptPiece[]): ConceptPiece[] {
  return rows.map((piece) =>
    piece.label || !isSeparatorText(piece.text) ? piece : { ...piece, label: 'ignore' },
  )
}

export function CodeConceptTrainer({
  kind,
  onError,
  onSuccess,
}: {
  kind: CodeConceptKind
  onError: (message: string) => void
  onSuccess: (message: string) => void
}) {
  const labels = kind === 'class' ? CLASS_LABELS : ROOM_LABELS
  const [example, setExample] = useState('')
  const [pieces, setPieces] = useState<ConceptPiece[]>([])
  const [concepts, setConcepts] = useState<CodeConcept[]>([])
  const [preview, setPreview] = useState<string[] | null>(null)
  const [emptyImport, setEmptyImport] = useState(false)
  const [previewed, setPreviewed] = useState(false)
  const [equivalentExample, setEquivalentExample] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wipeOpen, setWipeOpen] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const response = await timetableDataQualityApi.listConcepts(kind)
      setConcepts(response.concepts)
    } catch (err) {
      onError(extractErrorMessage(err, { loadError: 'Konzepte konnten nicht geladen werden' }))
    }
  }, [kind, onError])

  useEffect(() => {
    void load()
  }, [load])

  const handleDecompose = async () => {
    if (!example.trim()) return
    setBusy(true)
    setPreview(null)
    setPreviewed(false)
    setEquivalentExample(null)
    setLocalError(null)
    try {
      const result = await timetableDataQualityApi.decomposeConcept(kind, example.trim())
      setExample(result.example_text)
      setPieces(applySeparatorIgnore(result.pieces))
    } catch (err) {
      const message = extractErrorMessage(err, { loadError: 'Zerlegung fehlgeschlagen' })
      setLocalError(message)
      onError(message)
    } finally {
      setBusy(false)
    }
  }

  const mergeAt = (index: number) => {
    setPieces((current) => {
      if (index < 0 || index >= current.length - 1) return current
      const left = current[index]
      const right = current[index + 1]
      if (!left || !right) return current
      const merged: ConceptPiece = {
        start: left.start,
        end: right.end,
        text: left.text + right.text,
        label: left.label === right.label ? left.label : null,
      }
      return [...current.slice(0, index), merged, ...current.slice(index + 2)]
    })
    setPreviewed(false)
    setEquivalentExample(null)
  }

  const splitAt = (index: number, offset: number) => {
    setPieces((current) => {
      const piece = current[index]
      if (!piece || offset <= 0 || offset >= piece.text.length) return current
      const left: ConceptPiece = {
        start: piece.start,
        end: piece.start + offset,
        text: piece.text.slice(0, offset),
        label: null,
      }
      const right: ConceptPiece = {
        start: piece.start + offset,
        end: piece.end,
        text: piece.text.slice(offset),
        label: null,
      }
      return [...current.slice(0, index), left, right, ...current.slice(index + 1)]
    })
    setPreviewed(false)
    setEquivalentExample(null)
  }

  const setLabel = (index: number, label: string) => {
    setPieces((current) =>
      current.map((piece, i) => (i === index ? { ...piece, label: label || null } : piece)),
    )
    setPreviewed(false)
    setEquivalentExample(null)
    setLocalError(null)
  }

  const readyPieces = applySeparatorIgnore(pieces)
  const labeled = readyPieces.length > 0 && readyPieces.every((p) => p.label)

  const runPreview = async (rows: ConceptPiece[]) => {
    const result = await timetableDataQualityApi.previewConcept(kind, example, rows)
    setPreview(result.tokens)
    setEmptyImport(result.empty_import)
    setEquivalentExample(result.equivalent_example || null)
    setPreviewed(true)
    return result
  }

  const handlePreview = async () => {
    const rows = applySeparatorIgnore(pieces)
    if (!rows.length || rows.some((p) => !p.label)) {
      setLocalError('Jedes Stück braucht ein Label')
      return
    }
    setPieces(rows)
    setBusy(true)
    setLocalError(null)
    try {
      await runPreview(rows)
    } catch (err) {
      const message = extractErrorMessage(err, { loadError: 'Vorschau fehlgeschlagen' })
      setLocalError(message)
      onError(message)
      setPreviewed(false)
      setPreview(null)
      setEquivalentExample(null)
    } finally {
      setBusy(false)
    }
  }

  const handleSave = async () => {
    const rows = applySeparatorIgnore(pieces)
    if (!rows.length || rows.some((p) => !p.label)) {
      setLocalError('Jedes Stück braucht ein Label')
      return
    }
    setPieces(rows)
    setBusy(true)
    setLocalError(null)
    try {
      const previewResult = await runPreview(rows)
      if (previewResult.equivalent_example) {
        const message = `Gleichwertiges Konzept existiert bereits: ${previewResult.equivalent_example}`
        setLocalError(message)
        onError(message)
        return
      }
      await timetableDataQualityApi.createConcept(kind, example, rows)
      setExample('')
      setPieces([])
      setPreview(null)
      setPreviewed(false)
      onSuccess('Konzept gespeichert')
      await load()
    } catch (err) {
      const message = extractErrorMessage(err, { loadError: 'Speichern fehlgeschlagen' })
      setLocalError(message)
      onError(message)
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Konzept wirklich löschen?')) return
    try {
      await timetableDataQualityApi.deleteConcept(kind, id)
      await load()
    } catch (err) {
      onError(extractErrorMessage(err, { loadError: 'Löschen fehlgeschlagen' }))
    }
  }

  const handleWipe = async () => {
    setBusy(true)
    try {
      await timetableDataQualityApi.wipeConcepts()
      setWipeOpen(false)
      onSuccess('Schul-Regex gelöscht')
      await load()
    } catch (err) {
      onError(extractErrorMessage(err, { loadError: 'Wipe fehlgeschlagen' }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-6">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">Beispiel tippen</h3>
        <div className="flex gap-2">
          <input
            value={example}
            onChange={(e) => {
              setExample(e.target.value)
              setPieces([])
              setPreviewed(false)
              setLocalError(null)
            }}
            placeholder="Beispiel"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
          />
          <button
            onClick={handleDecompose}
            disabled={busy || !example.trim()}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Zerlegen
          </button>
        </div>
        {pieces.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pieces.map((piece, index) => (
              <div key={`${piece.start}-${piece.end}`} className="border border-gray-200 rounded-lg p-2 space-y-1">
                <div className="flex items-center gap-1 font-mono text-sm">
                  {index > 0 && (
                    <button type="button" className="text-xs text-blue-600" onClick={() => mergeAt(index - 1)}>
                      «
                    </button>
                  )}
                  <span className="inline-flex items-center">
                    {Array.from(piece.text).map((ch, charIndex) => (
                      <span key={`${piece.start}-${charIndex}`} className="inline-flex items-center">
                        {charIndex > 0 && (
                          <button
                            type="button"
                            className="px-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
                            title="Hier zerlegen"
                            onClick={() => splitAt(index, charIndex)}
                          >
                            |
                          </button>
                        )}
                        <span>{ch}</span>
                      </span>
                    ))}
                  </span>
                </div>
                <select
                  value={piece.label ?? ''}
                  onChange={(e) => setLabel(index, e.target.value)}
                  className="text-xs border border-gray-300 rounded px-1 py-0.5"
                >
                  <option value="">Label…</option>
                  {labels.map((row) => (
                    <option key={row.value} value={row.value}>
                      {row.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handlePreview}
            disabled={busy || pieces.length === 0}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Vorschau
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || pieces.length === 0 || Boolean(equivalentExample)}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Speichern
          </button>
        </div>
        {pieces.length > 0 && !labeled && (
          <p className="text-xs text-gray-500">Jedes Stück braucht ein Label. Trennzeichen sind schon «ignorieren».</p>
        )}
        {localError && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">{localError}</div>
        )}
        {previewed && (
          <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            {equivalentExample ? (
              <p>Gleichwertiges Konzept existiert bereits: {equivalentExample}</p>
            ) : null}
            {equivalentExample ? null : emptyImport ? (
              'Kein Import vorhanden. Speichern ist trotzdem erlaubt.'
            ) : preview && preview.length ? (
              `Trifft: ${preview.slice(0, 20).join(', ')}${preview.length > 20 ? '…' : ''}`
            ) : (
              'Keine Tokens des letzten Imports getroffen.'
            )}
            {equivalentExample && preview && preview.length ? (
              <p>{`Trifft bereits: ${preview.slice(0, 20).join(', ')}${preview.length > 20 ? '…' : ''}`}</p>
            ) : null}
          </div>
        )}
      </div>

      {concepts.length > 0 && (
        <table className="w-full text-sm divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Beispiel</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Labels</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {concepts.map((concept) => (
              <tr key={concept.id}>
                <td className="px-4 py-2 font-mono">{concept.example_text}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {concept.pieces.map((piece, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-gray-100">
                        {piece.text}={LABEL_TEXT[piece.label || ''] || piece.label}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleDelete(concept.id)} className="text-red-600 hover:text-red-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pt-2 border-t border-gray-200 flex flex-wrap gap-2">
        <button
          onClick={() => setWipeOpen(true)}
          className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
        >
          Schul-Regex löschen
        </button>
      </div>

      {wipeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-4 max-w-md space-y-3">
            <p className="text-sm text-gray-800">
              Alle Klassen- und Raum-Konzepte dieser Schule löschen? Zeiten, Listen, Lektionen und andere Schulen bleiben.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setWipeOpen(false)} className="px-3 py-2 text-sm border rounded-lg">
                Abbrechen
              </button>
              <button
                onClick={handleWipe}
                disabled={busy}
                className="px-3 py-2 text-sm text-white bg-red-600 rounded-lg disabled:opacity-50"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}
